import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import lodashCompact from 'lodash/fp/compact';
import queryString from 'query-string';

import {
  SR_API_ACTION_UTILITY_PARTNER_UPDATE_PROFILE,
  SR_API_ACTION_UTILITY_TENANT_BULK_VALIDATION,
  SR_API_ACTION_UTILITY_PARTNER_PM_SOFTWARE_UPSERT,
  SR_API_ACTION_UTILITY_PARTNER_AGREE_TO_TOS,
  SR_API_ACTION_UTILITY_PROVIDER_OVERRIDE_URL_VALIDATE,
  SR_API_ACTION_UTILITY_PROVIDER_OVERRIDE_UPSERT,
} from 'api/utilityActionTypes';

import {
  PROPERTY_MANAGEMENT_SOFTWARE_NAME_APPFOLIO,
  PROPERTY_MANAGEMENT_SOFTWARE_NAME_PROPERTYWARE,
  PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_REPORT,
  PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_API,
  PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_SCRAPER,
} from 'common/propertyManagementSoftwareConstants';

import {
  UTILITY_PARTNER_GROUP_ID_RELIANT,
  UTILITY_PROVIDER_ID_RELIANT,
  UTILITY_PROPERTY_MANAGEMENT_SOFTWARE_OTHER_ID,
} from 'common/utility/constants';

import {
  createFormValidationFn,
  createInitialValues,
  showFieldIf,
  scrollToFormField,
} from 'client/helpers/formikHelpers';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrMarkdown, { SR_MARKDOWN_THEME_GREYCLIFF } from 'client/components/SrMarkdown';
import { SR_CHECKBOX_THEME_BOX } from 'client/components/Checkbox';
import SrFormikField, { SR_FIELD_THEME_ADOBE_UTILITY_PROFIT } from 'client/components/SrFormikField';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrLink from 'client/components/SrLink/SrLink';
import SrTextGreycliff from 'client/components/SrTextGreycliff';
import UpSelfOnboardingHeader from 'client/components/UPSelfOnboarding/UpSelfOnboardingHeader';

import './UpSelfOnboarding.less';
import { useLocation } from 'react-router';

const agreementFields = ['agreementForTermsOfService', 'agreementForInternetSupport'];

const propertySoftwareToActionsMapping = {
  [PROPERTY_MANAGEMENT_SOFTWARE_NAME_APPFOLIO]: [
    'Actions -> Save Layout',
    'Created new scheduled report',
    'Recipient sync@utilityprofit.com',
    'Format CSV',
    'Frequency Daily',
    'Saved schedule report',
  ],
  [PROPERTY_MANAGEMENT_SOFTWARE_NAME_PROPERTYWARE]: [
    'Scheduled email',
    'Frequency is daily',
    'Format is Excel',
  ],
};

const getSubdomainFromUrl = (url) => {
  if (!url) {
    return '';
  }

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    const parts = hostname.split('.');

    return parts.length > 2 ? parts.slice(0, -2).join('.') : '';
  } catch (error) {
    return '';
  }
};

const getFormFields = ({
  utilityPartner,
  utilityPartnerGroup,
  propertyManagementSoftware,
  propertyManagementSoftwareList,
  onChangePropertyManagerSoftware,
  onValidateCsv,
  onValidateProviderOverrideUrl,
  isCsvValid,
}) => {
  const isIntegrationTypeAPI =
    propertyManagementSoftware?.integrationType ===
    PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_API;
  const isIntegrationTypeReport =
    propertyManagementSoftware?.integrationType ===
    PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_REPORT;
  const isIntegrationTypeScraper =
    propertyManagementSoftware?.integrationType ===
    PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_SCRAPER;

  const choices = propertyManagementSoftwareList.map((software) => ({
    value: software.id,
    label: software.name,
  }));

  choices.sort((a, b) => {
    if (a.label === 'Other') return 1;
    if (b.label === 'Other') return -1;
    return a.label.localeCompare(b.label);
  });

  let csvFileMessage = null;
  if (typeof isCsvValid === 'boolean') {
    csvFileMessage = isCsvValid ? (
      'Report looks good!'
    ) : (
      <>
        <SrText font="greycliff" color="orange300" weight="800" size="21">
          Report looks a bit off...
        </SrText>
      </>
    );
  }

  const formFields = lodashCompact([
    showFieldIf(utilityPartnerGroup?.id === UTILITY_PARTNER_GROUP_ID_RELIANT, {
      id: 'reliantReferralLink',
      title: `${utilityPartnerGroup?.name} Referral Link for Renters`,
      subtitle: `Enter your unique ${utilityPartnerGroup?.name} referral link to direct your renters to the correct page.`,
      type: 'short_text',
      properties: {
        placeholder: 'Enter url here...',
        validate: onValidateProviderOverrideUrl,
      },
      validations: ['url', 'required'],
    }),
    {
      id: 'propertyManagementSoftwareId',
      title: 'Which software do you use?',
      type: 'select',
      properties: {
        placeholder: 'Select your PM software...',
        choices: choices,
        widget:
          propertyManagementSoftware?.enabled === true ? (
            <SrBox margin="16px 0 30px">
              <SrText font="greycliff" weight="700" color="green" fontSize="21px">
                Please follow the steps to the right 👉
              </SrText>
              <br />
              <SrText font="greycliff" weight="700" fontSize="21px">
                It's easy to do!
              </SrText>
            </SrBox>
          ) : null,
        onChange: onChangePropertyManagerSoftware,
      },
      validations: ['required'],
    },
    showFieldIf(propertyManagementSoftware?.id === UTILITY_PROPERTY_MANAGEMENT_SOFTWARE_OTHER_ID, {
      id: 'propertyManagementSoftwareOther',
      title: 'Software name',
      type: 'short_text',
      validations: ['required'],
    }),
    showFieldIf(propertyManagementSoftware?.enabled === true && isIntegrationTypeReport, {
      id: 'csvFileUrl',
      title: 'Validate the report to continue:',
      type: 'file_single',
      properties: {
        placeholder: 'Tap or drop file to upload',
        fileAccept:
          'text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // eslint-disable-line max-len
        filePath: `/up-uploads/csv/test-${utilityPartner.id}-{uuid}`,
        isPrivate: true,
        onChange: onValidateCsv,
        className: isCsvValid === false ? 'selfOnboardingCsvFileUpload' : null,
        successMessage: csvFileMessage,
        successMessageTextClassName: isCsvValid && 'animate__animated animate__tada',
      },
      validations: ['required'],
    }),
    showFieldIf(isCsvValid && isIntegrationTypeReport, {
      id: 'actions',
      title: 'I confirm I already did these:',
      type: 'multiple_choice',
      properties: {
        choices: (propertySoftwareToActionsMapping[propertyManagementSoftware?.name] || []).map(
          (action) => ({
            value: action,
            label: action,
          }),
        ),
        multipleSelection: true,
        fieldType: SR_CHECKBOX_THEME_BOX,
        autoFitWidth: '100%',
        checkboxClassName: 'selfOnboardingCheckbox',
      },
      validations: ['required::Please check all of these.', 'checklistCompleted'],
    }),
    showFieldIf(isIntegrationTypeAPI, {
      id: 'apiKey',
      title: 'API Key',
      type: 'short_text',
      validations: ['required'],
      properties: {
        placeholder: 'paste your API key here...',
      },
    }),
    showFieldIf(isIntegrationTypeAPI, {
      id: 'apiSecret',
      title: 'API Secret',
      type: 'short_text',
      validations: ['required'],
      properties: {
        placeholder: 'paste your API secret here...',
      },
    }),
    showFieldIf(
      isIntegrationTypeAPI ||
      (isIntegrationTypeScraper && propertyManagementSoftware?.name === 'Buildium'),
      {
        id: 'subdomain',
        title:
          isIntegrationTypeAPI
            ? `${propertyManagementSoftware?.name} Url`
            : `Enter your ${propertyManagementSoftware?.name} Url`,
        type: 'short_text',
        validations: ['required', 'url'],
        properties: {
          placeholder:
            isIntegrationTypeAPI
              ? 'https://yourcompany.rentvine.com'
              : 'https://yourcompany.managebuilding.com',
        },
      }
    ),
    showFieldIf(isIntegrationTypeScraper && propertyManagementSoftware?.name === 'Buildium', {
      id: 'agreementForBuildiumInvitation',
      title: 'Confirm:',
      type: 'simple_checkbox',
      properties: {
        noMargin: true,
        children: (
          <SrBox textAlign="left">
            <SrTextGreycliff weight="700" lineHeight="5px" size="18px">
              I invited Utility Profit to my Buildium account as a Property Manager user.
            </SrTextGreycliff>
          </SrBox>
        ),
      },
      validations: ['requiredCheck'],
    }),
    {
      id: 'agreementForTermsOfService',
      type: 'simple_checkbox',
      properties: {
        noMargin: true,
        children: (
          <SrBox textAlign="left">
            <SrTextGreycliff weight="500" lineHeight="20px" size="13">
              I agree to the{' '}
              <SrLink
                to="https://www.utilityprofit.com/terms-of-service"
                target="_blank"
                color="blue300"
                style={{ textDecoration: 'underline' }}
              >
                Terms of Service
              </SrLink>{' '}
              which allows UtilityProfit to contact my tenants, enroll my properties in discount
              internet, and protect my data.
            </SrTextGreycliff>
          </SrBox>
        ),
      },
      validations: ['legal', 'required'],
    },
    {
      id: 'agreementForInternetSupport',
      type: 'simple_checkbox',
      properties: {
        noMargin: true,
        children: (
          <SrBox textAlign="left">
            <SrTextGreycliff weight="500" lineHeight="20px" size="13">
              I understand if tenants have issues setting up internet, I can direct tenants to
              contact UtilityProfit at their support phone number.
            </SrTextGreycliff>
          </SrBox>
        ),
      },
      validations: ['legal', 'required'],
    },
  ]);

  return formFields;
};

const UpSelfOnboardingConnectStep = ({
  onNext,
  activeIndex = 4,
  utilityPartner,
  utilityPartnerGroup,
  propertyManagementSoftwareList,
  showSkipForNow,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isCsvValid, setIsCsvValid] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const queryUrl = queryString.parse(location.search);
  const theme = utilityPartnerGroup?.defaultTheme || {};
  const preSelectedPmsSoftware = queryUrl.pmsId || null;

  const onValidateProviderOverrideUrl = (url) => {
    const errorMessage = 'Hm, that URL has already been taken by another account. Please submit a new url.';
    const invalidUrlErrorMessage = 'Please enter a valid URL. The URL must include either http:// or https://';

    if (!url) {
      return;
    }

    return new Promise((resolve, reject) => {
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_PROVIDER_OVERRIDE_URL_VALIDATE,
        payload: { url, providerId: UTILITY_PROVIDER_ID_RELIANT },
        onSuccess: ({ isValid }) => {
          if (isValid) {
            resolve();
          } else {
            reject(errorMessage);
          }
        },
        onError: (error) => {
          if (error.message === 'body/url must match format "uri"') {
            reject(invalidUrlErrorMessage);
          }
          else {
            reject(errorMessage);
          }
        },
      });
    });
  };

  const getIntegrationPayloadFromFormValues = (formValues) => {
    const propertyManagementSoftware = propertyManagementSoftwareList.find((software) => {
      return software.id === parseInt(formValues.propertyManagementSoftwareId, 10);
    });

    switch (propertyManagementSoftware?.integrationType) {
      case PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_API:
        return {
          utilityPartnerId: utilityPartner.id,
          propertyManagementSoftwareId: propertyManagementSoftware.id,
          subdomain: getSubdomainFromUrl(formValues.subdomain),
          apiKey: formValues.apiKey,
          apiSecret: formValues.apiSecret,
        };
      case PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_SCRAPER:
        return {
          utilityPartnerId: utilityPartner.id,
          propertyManagementSoftwareId: propertyManagementSoftware.id,
          subdomain: getSubdomainFromUrl(formValues.subdomain),
        };
      default:
        return {
          utilityPartnerId: utilityPartner.id,
          updates: {
            propertyManagementSoftwareId: parseInt(formValues.propertyManagementSoftwareId, 10),
            propertyManagementSoftwareOther: formValues.propertyManagementSoftwareOther,
          },
        };
    }
  };

  const onConnect = async (formValues) => {
    setIsSubmitting(true);
    setErrorMessage(null);

    const propertyManagementSoftware = propertyManagementSoftwareList.find((software) => {
      return software.id === parseInt(formValues.propertyManagementSoftwareId, 10);
    });

    const isIntegrationTypeAPI =
      propertyManagementSoftware?.integrationType ===
      PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_API;
    const isIntegrationTypeScraper =
      propertyManagementSoftware?.integrationType ===
      PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_SCRAPER;

    const actionType =
      (isIntegrationTypeAPI || isIntegrationTypeScraper)
        ? SR_API_ACTION_UTILITY_PARTNER_PM_SOFTWARE_UPSERT
        : SR_API_ACTION_UTILITY_PARTNER_UPDATE_PROFILE;

    const payload = getIntegrationPayloadFromFormValues(formValues);

    await dispatch(requestUtilityApi, {
      action: actionType,
      payload: payload,
      onError: (error) => {
        if (error.message.includes('already exists')) {
          setErrorMessage(
            'Oops! Looks like you already have an integration with us.' +
            'Please reach out to us at support@utilityprofit.com if you need help.',
          );
        } else {
          setErrorMessage(error.message);
        }
        setIsSubmitting(false);
      },
      onSuccess: async () => {
        if (formValues.reliantReferralLink) {
          await dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_PROVIDER_OVERRIDE_UPSERT,
            payload: {
              partnerId: utilityPartner.id,
              providerId: UTILITY_PROVIDER_ID_RELIANT,
              url: formValues.reliantReferralLink,
            },
          });
        }
        if (formValues.agreementForTermsOfService) {
          await dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_PARTNER_AGREE_TO_TOS,
            payload: {
              utilityPartnerId: utilityPartner.id,
            },
          });
        }
        setIsSubmitting(false);
        onNext();
      },
    });
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: getFormFields({
          utilityPartner,
          propertyManagementSoftwareList,
          propertyManagementSoftware: preSelectedPmsSoftware ? propertyManagementSoftwareList.find((software) => {
            return software.id === parseInt(preSelectedPmsSoftware, 10);
          }) : null,
        }),
        defaultValues: {
          propertyManagementSoftwareId: preSelectedPmsSoftware || null,
          csvFileUrl: null,
          apiKey: null,
          apiSecret: null,
          subdomain: null,
        },
      })}
      validate={(formValues) => {
        const formFields = getFormFields({
          utilityPartner,
          propertyManagementSoftware: propertyManagementSoftwareList.find((software) => {
            return (
              software.id === parseInt(formValues.propertyManagementSoftwareId, 10) ||
              software.id === formValues.propertyManagementSoftwareId
            );
          }),
          propertyManagementSoftwareList,
          isCsvValid,
        });
        return createFormValidationFn(formFields)(formValues);
      }}
      onSubmit={onConnect}
      render={(formikBag) => {
        const propertyManagementSoftware = propertyManagementSoftwareList.find((software) => {
          return (
            software.id === parseInt(formikBag.values.propertyManagementSoftwareId, 10) ||
            software.id === formikBag.values.propertyManagementSoftwareId
          );
        });
        const isIntegrationTypeAPI =
          propertyManagementSoftware?.integrationType ===
          PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_API;

        const isIntegrationTypeScraper =
          propertyManagementSoftware?.integrationType ===
          PROPERTY_MANAGEMENT_SOFTWARE_INTEGRATION_TYPE_SCRAPER;

        const onValidateCsv = async (fileUrl) => {
          if (!formikBag.values.propertyManagementSoftwareId && !formikBag.values.csvFileUrl) {
            return;
          }
          setIsCsvValid(null);

          dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_TENANT_BULK_VALIDATION,
            payload: {
              utilityPartnerId: utilityPartner.id,
              propertyManagementSoftwareId: propertyManagementSoftware.id,
              fileUrl: fileUrl,
            },
            onSuccess: (isValid) => {
              setIsCsvValid(isValid);
              setTimeout(() => {
                scrollToFormField({
                  formFieldId: 'actions',
                });
              }, 100);
            },
            onError: () => {
              setIsCsvValid(false);
            },
          });
        };

        const onChangePropertyManagerSoftware = (event) => {
          const reliantReferralLink = formikBag.values.reliantReferralLink;
          formikBag.resetForm();
          if (reliantReferralLink) {
            formikBag.setFieldValue('reliantReferralLink', reliantReferralLink);
          }
          formikBag.setFieldValue('propertyManagementSoftwareId', event.target.value);
          setIsCsvValid(null);
        };

        const formFields = getFormFields({
          utilityPartner,
          utilityPartnerGroup,
          propertyManagementSoftware,
          propertyManagementSoftwareList,
          onChangePropertyManagerSoftware,
          onValidateCsv,
          onValidateProviderOverrideUrl,
          isCsvValid,
        });

        return (
          <div styleName="layout">
            <div styleName="left">
              <UpSelfOnboardingHeader
                activeIndex={activeIndex}
                partnerGroupLogoUrl={utilityPartnerGroup?.logoUrl}
                partnerGroupPrimaryColor={theme.primaryColor}
                partnerGroupSecondaryColor={theme.secondaryColor}
                partnerGroupInactiveColor={theme.inactiveTextColor}
              />
              <div styleName="lineSeparator" />
              <Form styleName="form">
                <span styleName="titleText">Easily integrate.</span>
                <span styleName="subTitleText">It's free, simple for anyone to do, and fast!</span>
                <SrBox margin="26px 0 16px">
                  {formFields.filter((formField) => !agreementFields.includes(formField.id)).map((formField, index) => (
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE_UTILITY_PROFIT}
                      key={formField.id}
                      margin={index === 0 ? '0' : null}
                    />
                  ))}
                </SrBox>
                {propertyManagementSoftware?.enabled === false && (
                  <>
                    <SrBox marginBottom="22px">
                      <SrText font="greycliff" weight="700" color="green">
                        We'll let you know when our {propertyManagementSoftware.name} integration is
                        ready.
                      </SrText>{' '}
                      <SrText font="greycliff" weight="500">
                        Let's breeze past this step for now!
                      </SrText>
                    </SrBox>
                    <SrBox margin="10px 0 16px">
                      {formFields.filter((formField) => agreementFields.includes(formField.id)).map((formField, index) => (
                        <SrFormikField
                          formField={formField}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE_UTILITY_PROFIT}
                          key={formField.id}
                          margin={index === 0 ? '0' : null}
                        />
                      ))}
                    </SrBox>
                    <SrButton
                      theme={SR_BUTTON_THEME_ADOBE_GREEN}
                      hasBoxShadow
                      type="submit"
                      themeOverrides={{
                        backgroundColor: theme?.buttonPrimaryColor,
                        color: theme?.buttonPrimaryTextColor,
                      }}
                    >
                      Skip for now
                    </SrButton>
                  </>
                )}
                {(isCsvValid === true || isIntegrationTypeAPI || isIntegrationTypeScraper) &&
                  propertyManagementSoftware?.enabled === true && (
                    <SrBox margin="36px 0 16px">
                      {errorMessage && (
                        <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED} marginBottom="16px">
                          {errorMessage}
                        </SrErrorMessage>
                      )}
                      <SrBox margin="0 0 16px">
                        {formFields.filter((formField) => agreementFields.includes(formField.id)).map((formField, index) => (
                          <SrFormikField
                            formField={formField}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_UTILITY_PROFIT}
                            key={formField.id}
                            margin={index === 0 ? '0' : null}
                          />
                        ))}
                      </SrBox>
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREEN}
                        hasBoxShadow
                        type="submit"
                        disabled={isSubmitting}
                        themeOverrides={{
                          backgroundColor: theme?.buttonPrimaryColor,
                          color: theme?.buttonPrimaryTextColor,
                        }}
                      >
                        Continue
                      </SrButton>
                    </SrBox>
                  )}
                {showSkipForNow && (
                  <SrBox>
                    <span styleName="skipForNow">Want to do this later?</span>{' '}
                    <span styleName="skipForNow link" onClick={onNext}>
                      Skip for now
                    </span>
                  </SrBox>
                )}
              </Form>
            </div>
            <div styleName="right">
              <div styleName="content connectStep fadeIn">
                <div styleName="row">
                  {!propertyManagementSoftware && (
                    <>
                      <span styleName="titleText">The easiest integration you'll ever do:</span>
                      <img
                        src="/images/up-self-onboarding-connect-pms.png"
                        alt="List of PM software"
                        styleName="pmsListImg"
                      />
                    </>
                  )}
                  {propertyManagementSoftware?.enabled === false && (
                    <>
                      <span styleName="titleText">Integration available soon</span>
                      <div styleName="integrationNotReadyBox">
                        {propertyManagementSoftware.logoUrl && (
                          <div
                            style={{
                              backgroundImage: `url(${propertyManagementSoftware.logoUrl})`,
                            }}
                            styleName="logoImg"
                          />
                        )}
                      </div>
                    </>
                  )}
                  {propertyManagementSoftware?.enabled === true && (
                    <>
                      <div styleName="markdownBox">
                        <SrMarkdown
                          escapeHtml={false}
                          source={propertyManagementSoftware.connectMarkdown}
                          theme={SR_MARKDOWN_THEME_GREYCLIFF}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

UpSelfOnboardingConnectStep.displayName = 'UpSelfOnboardingConnectStep';

UpSelfOnboardingConnectStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  utilityPartner: PropTypes.object.isRequired,
  utilityPartnerGroup: PropTypes.object,
  activeIndex: PropTypes.number,
  propertyManagementSoftwareList: PropTypes.array.isRequired,
};

export default UpSelfOnboardingConnectStep;
